// src/components/Visualization/ScatterAnalysis.js
import React, { useState, useMemo } from 'react';
import { 
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ZAxis, Cell, Label, ReferenceLine
} from 'recharts';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * ScatterAnalysis - Gráfico de dispersão para análise avançada de correlações
 * 
 * @param {Object} props
 * @param {Array} props.data - Array de dados para o gráfico
 * @param {string} props.xAxisDataKey - Chave para o eixo X
 * @param {string} props.yAxisDataKey - Chave para o eixo Y
 * @param {string} props.sizeKey - Chave para determinar o tamanho dos pontos (opcional)
 * @param {string} props.nameKey - Chave para identificar cada ponto
 * @param {string} props.colorKey - Chave para determinar a cor dos pontos (opcional)
 * @param {string} props.xAxisLabel - Título do eixo X
 * @param {string} props.yAxisLabel - Título do eixo Y
 * @param {Function} props.xAxisFormatter - Formatar valores do eixo X
 * @param {Function} props.yAxisFormatter - Formatar valores do eixo Y
 * @param {Function} props.tooltipFormatter - Função personalizada para tooltip
 * @param {Array} props.quadrantLabels - Rótulos para os quadrantes [TL, TR, BR, BL]
 * @param {Array} props.colors - Cores personalizadas para os pontos
 * @param {boolean} props.showQuadrants - Mostrar linhas divisórias de quadrantes
 * @param {boolean} props.showTrends - Mostrar linhas de tendência
 * @param {string} props.height - Altura do gráfico
 * @param {Array} props.annotations - Anotações ou pontos especiais para destacar
 * @param {boolean} props.showControls - Mostrar controles adicionais para o gráfico
 * @param {Array} props.filters - Opções de filtro para os dados
 */
const ScatterAnalysis = ({
  data = [],
  xAxisDataKey,
  yAxisDataKey,
  sizeKey,
  nameKey = 'name',
  colorKey,
  xAxisLabel = '',
  yAxisLabel = '',
  xAxisFormatter = value => value,
  yAxisFormatter = value => value,
  tooltipFormatter,
  quadrantLabels = ['', '', '', ''],
  colors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'],
  showQuadrants = false,
  showTrends = false,
  height = 400,
  annotations = [],
  showControls = true,
  filters = []
}) => {
  // Estados para controles e filtros
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [showSettings, setShowSettings] = useState(false);

  // Calcular médias e outros valores de referência
  const axisInfo = useMemo(() => {
    if (!data.length) return { xAvg: 0, yAvg: 0, xMax: 0, yMax: 0, xMin: 0, yMin: 0 };
    
    let xSum = 0;
    let ySum = 0;
    let xMax = -Infinity;
    let yMax = -Infinity;
    let xMin = Infinity;
    let yMin = Infinity;
    
    data.forEach(item => {
      const xVal = item[xAxisDataKey];
      const yVal = item[yAxisDataKey];
      
      xSum += xVal;
      ySum += yVal;
      
      xMax = Math.max(xMax, xVal);
      yMax = Math.max(yMax, yVal);
      xMin = Math.min(xMin, xVal);
      yMin = Math.min(yMin, yVal);
    });
    
    return {
      xAvg: xSum / data.length,
      yAvg: ySum / data.length,
      xMax,
      yMax,
      xMin,
      yMin
    };
  }, [data, xAxisDataKey, yAxisDataKey]);

  // Aplicar filtros e ordenação aos dados
  const processedData = useMemo(() => {
    // Filtrar dados se necessário
    let filteredData = [...data];
    if (activeFilter !== 'all' && colorKey) {
      filteredData = data.filter(item => item[colorKey] === activeFilter);
    }
    
    // Ordenar dados se necessário
    if (sortBy) {
      filteredData.sort((a, b) => {
        const valA = a[sortBy];
        const valB = b[sortBy];
        
        if (sortDirection === 'asc') {
          return valA - valB;
        } else {
          return valB - valA;
        }
      });
    }
    
    return filteredData;
  }, [data, activeFilter, colorKey, sortBy, sortDirection]);

  // Componente personalizado para Tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      
      return (
        <div className="bg-white p-3 shadow-lg border border-gray-200 rounded-lg">
          <p className="font-medium text-sm mb-1">{dataPoint[nameKey]}</p>
          <div className="space-y-1 text-xs">
            <p className="flex justify-between">
              <span className="text-gray-600 mr-3">{xAxisLabel || xAxisDataKey}:</span>
              <span className="font-semibold">{xAxisFormatter(dataPoint[xAxisDataKey])}</span>
            </p>
            <p className="flex justify-between">
              <span className="text-gray-600 mr-3">{yAxisLabel || yAxisDataKey}:</span>
              <span className="font-semibold">{yAxisFormatter(dataPoint[yAxisDataKey])}</span>
            </p>
            {sizeKey && (
              <p className="flex justify-between">
                <span className="text-gray-600 mr-3">{sizeKey}:</span>
                <span className="font-semibold">{dataPoint[sizeKey]}</span>
              </p>
            )}
            {/* Exibir quaisquer campos adicionais importantes */}
            {dataPoint.additionalInfo && (
              <p className="flex justify-between">
                <span className="text-gray-600 mr-3">Info:</span>
                <span className="font-semibold">{dataPoint.additionalInfo}</span>
              </p>
            )}
          </div>
        </div>
      );
    }
    
    return null;
  };

  // Lógica para determinar cores dos pontos
  const getPointColor = (entry, index) => {
    if (colorKey && entry[colorKey] !== undefined) {
      // Se tivermos mapeamento de categorias para cores
      const categories = [...new Set(data.map(item => item[colorKey]))];
      const categoryIndex = categories.indexOf(entry[colorKey]);
      return colors[categoryIndex % colors.length];
    }
    
    // Fallback para cor baseada no índice
    return colors[index % colors.length];
  };

  // Handler para alterar ordenação
  const handleSort = (key) => {
    if (sortBy === key) {
      // Inverter direção se clicar no mesmo campo
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Novo campo de ordenação, começar com descendente
      setSortBy(key);
      setSortDirection('desc');
    }
  };

  // Renderizar indicador de direção de ordenação
  const renderSortIndicator = (key) => {
    if (sortBy !== key) return null;
    
    return sortDirection === 'asc' 
      ? <ChevronUp size={14} className="inline ml-1" />
      : <ChevronDown size={14} className="inline ml-1" />;
  };

  return (
    <div className="card overflow-hidden">
      {/* Cabeçalho com controles */}
      {showControls && (
        <div className="card-header">
          <div>
            <h3 className="card-title">Análise de Correlação</h3>
            <p className="text-sm text-gray-500">
              {xAxisLabel || xAxisDataKey} vs {yAxisLabel || yAxisDataKey}
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Filtros */}
            {filters.length > 0 && (
              <select 
                className="form-select text-sm"
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
              >
                <option value="all">Todos</option>
                {filters.map((filter, idx) => (
                  <option key={`filter-${idx}`} value={filter.value}>
                    {filter.label}
                  </option>
                ))}
              </select>
            )}
            
            {/* Controles de ordenação */}
            <div className="flex items-center space-x-2">
              <button 
                className={`text-xs px-3 py-1 rounded ${
                  sortBy === xAxisDataKey ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                }`}
                onClick={() => handleSort(xAxisDataKey)}
              >
                {xAxisLabel || xAxisDataKey}
                {renderSortIndicator(xAxisDataKey)}
              </button>
              
              <button 
                className={`text-xs px-3 py-1 rounded ${
                  sortBy === yAxisDataKey ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                }`}
                onClick={() => handleSort(yAxisDataKey)}
              >
                {yAxisLabel || yAxisDataKey}
                {renderSortIndicator(yAxisDataKey)}
              </button>
              
              {sizeKey && (
                <button 
                  className={`text-xs px-3 py-1 rounded ${
                    sortBy === sizeKey ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                  }`}
                  onClick={() => handleSort(sizeKey)}
                >
                  {sizeKey}
                  {renderSortIndicator(sizeKey)}
                </button>
              )}
            </div>
            
            {/* Botão para configurações avançadas */}
            <button 
              className={`p-1 rounded ${showSettings ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}
              onClick={() => setShowSettings(!showSettings)}
            >
              {showSettings ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>
      )}
      
      {/* Configurações avançadas (expandível) */}
      {showSettings && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Mostrar quadrantes
              </label>
              <div className="flex items-center space-x-4">
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="form-checkbox"
                    checked={showQuadrants}
                    // onChange para atualizar via setState em um componente real
                  />
                  <span className="ml-2 text-sm">Ativar</span>
                </label>
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Mostrar linha de tendência
              </label>
              <div className="flex items-center space-x-4">
                <label className="inline-flex items-center">
                  <input 
                    type="checkbox" 
                    className="form-checkbox"
                    checked={showTrends}
                    // onChange para atualizar via setState em um componente real
                  />
                  <span className="ml-2 text-sm">Ativar</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Gráfico */}
      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart
            margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            
            <XAxis 
              type="number" 
              dataKey={xAxisDataKey} 
              name={xAxisLabel || xAxisDataKey}
              tickFormatter={xAxisFormatter}
              domain={['auto', 'auto']}
              label={{
                value: xAxisLabel || xAxisDataKey,
                position: 'bottom',
                offset: 0,
                style: { fontSize: 12, fill: '#6B7280' }
              }}
            />
            
            <YAxis 
              type="number" 
              dataKey={yAxisDataKey} 
              name={yAxisLabel || yAxisDataKey} 
              tickFormatter={yAxisFormatter}
              domain={['auto', 'auto']}
              label={{
                value: yAxisLabel || yAxisDataKey,
                angle: -90,
                position: 'left',
                offset: 0,
                style: { fontSize: 12, fill: '#6B7280' }
              }}
            />
            
            {sizeKey && <ZAxis type="number" dataKey={sizeKey} range={[60, 400]} />}
            
            <Tooltip content={<CustomTooltip />} />
            
            {colorKey && <Legend />}
            
            {/* Linhas de referência para os quadrantes */}
            {showQuadrants && (
              <>
                <ReferenceLine 
                  x={axisInfo.xAvg} 
                  stroke="#6B7280" 
                  strokeDasharray="3 3" 
                  strokeOpacity={0.5} 
                />
                <ReferenceLine 
                  y={axisInfo.yAvg} 
                  stroke="#6B7280" 
                  strokeDasharray="3 3" 
                  strokeOpacity={0.5} 
                />
                
                {/* Adicionar rótulos para quadrantes */}
                {quadrantLabels[0] && (
                  <Label
                    position="center"
                    x={(axisInfo.xMin + axisInfo.xAvg) / 2}
                    y={(axisInfo.yAvg + axisInfo.yMax) / 2}
                    fontSize={12}
                    fill="#6B7280"
                    value={quadrantLabels[0]}
                  />
                )}
                {quadrantLabels[1] && (
                  <Label
                    position="center"
                    x={(axisInfo.xAvg + axisInfo.xMax) / 2}
                    y={(axisInfo.yAvg + axisInfo.yMax) / 2}
                    fontSize={12}
                    fill="#6B7280"
                    value={quadrantLabels[1]}
                  />
                )}
                {quadrantLabels[2] && (
                  <Label
                    position="center"
                    x={(axisInfo.xAvg + axisInfo.xMax) / 2}
                    y={(axisInfo.yMin + axisInfo.yAvg) / 2}
                    fontSize={12}
                    fill="#6B7280"
                    value={quadrantLabels[2]}
                  />
                )}
                {quadrantLabels[3] && (
                  <Label
                    position="center"
                    x={(axisInfo.xMin + axisInfo.xAvg) / 2}
                    y={(axisInfo.yMin + axisInfo.yAvg) / 2}
                    fontSize={12}
                    fill="#6B7280"
                    value={quadrantLabels[3]}
                  />
                )}
              </>
            )}
            
            {/* Gráfico de dispersão */}
            <Scatter
              data={processedData}
              name={nameKey}
              fill="#8884d8"
            >
              {processedData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getPointColor(entry, index)} 
                />
              ))}
            </Scatter>
            
            {/* Pontos de destaque (anotações) */}
            {annotations.map((annotation, index) => (
              <Scatter
                key={`annotation-${index}`}
                data={[annotation]}
                name={annotation.label || 'Destaque'}
                fill={annotation.color || '#FF0000'}
                shape="star"
                line={{ stroke: annotation.color || '#FF0000', strokeWidth: 2 }}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ScatterAnalysis;
